import React from "react"
import { useGetOrgHourReportAuditableMembershipsQuery } from "../../memberships/api"
import type { HourReportAuditableMembershipViewModel } from "../../memberships/api/hour-report-auditable-membership"
import type { HourReportFormMembershipOption } from "../api/schemas"
import { useHourReportFormOptions } from "./use-form-options"

export type ReporterMembershipOption = {
  id: string
  fullname: string
}

export const mergeReportedHoursMembershipOptions = ({
  auditableMemberships,
  membershipOptions,
  canReportOthersHours,
}: MergeReportedHoursMembershipOptionsArgs): ReporterMembershipOption[] => {
  const membershipsById = new Map<string, ReporterMembershipOption>(
    auditableMemberships.map((membership) => [membership.id, membership])
  )

  if (canReportOthersHours) {
    membershipOptions.forEach((membership) => {
      if (!membershipsById.has(membership.id)) {
        membershipsById.set(membership.id, {
          id: membership.id,
          fullname: membership.userFullname,
        })
      }
    })
  }

  return Array.from(membershipsById.values())
}

export const useReportedHoursMembershipOptions = ({
  orgId,
  canReportOthersHours,
}: UseReportedHoursMembershipOptionsArgs) => {
  const { data: auditableMemberships = [], isLoading: isLoadingAuditableMemberships } =
    useGetOrgHourReportAuditableMembershipsQuery(orgId, { skip: !orgId })
  const { membershipOptions, meta: membershipOptionsMeta } = useHourReportFormOptions()

  const memberships = React.useMemo(
    () =>
      mergeReportedHoursMembershipOptions({
        auditableMemberships,
        membershipOptions,
        canReportOthersHours,
      }),
    [auditableMemberships, canReportOthersHours, membershipOptions]
  )

  return {
    memberships,
    isLoading: isLoadingAuditableMemberships || (canReportOthersHours && membershipOptionsMeta.isLoading),
  }
}

type MergeReportedHoursMembershipOptionsArgs = {
  auditableMemberships: HourReportAuditableMembershipViewModel[]
  membershipOptions: HourReportFormMembershipOption[]
  canReportOthersHours: boolean
}

type UseReportedHoursMembershipOptionsArgs = {
  orgId: string
  canReportOthersHours: boolean
}
